/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { compact, filter, isEmpty, map, partition, reject } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Text } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { getPersona, isClosingPersona } from 'blb-persona/index';

import {
  FormTask,
  DocUploadTask,
  OptionalUploadTask,
  RedirectTask,
  ShareTask,
  ScheduleClosingAppointmentTask,
  SelectRONProviderTask,
  TaggingTask,
  ReleaseClosingDocumentsTask,
  TaskError,
  CancelClosingDocumentsTask,
} from '.';
import { DashboardSection } from '../DashboardSection';
import { generateTaskDescriptionKey, TaskDescription } from '../TaskDescription';
import ClosingType = EClose.ClosingType;

const styles = StyleSheet.create({
  noOpenTaskContainer: {
    marginBottom: 10,
  },
  preserveWhiteSpace: {
    whiteSpace: 'pre',
  },
  selectWrapper: {
    marginBottom: 32,
    maxWidth: '50%',
  },
  taskLabel: {
    display: 'inline-block',
    marginBottom: 8,
  },
});

export const getPendingTasks = (
  initialize: () => Promise<any>,
  principalType: BlendBridge.PrincipalType,
  openTasks?: BlendBridge.API.Task[],
  specialInstructions?: string,
  featureFlags?: BlendBridge.API.DCSFlags,
  view?: BlendBridge.TaskViewType,
  closingType?: ClosingType,
): DashboardSection.Props['sectionConfigs'] => {
  const persona = getPersona(principalType);
  const lenderInstructions =
    specialInstructions && isClosingPersona(persona) && persona.showSpecialInstructions()
      ? {
          content: (
            <p className={css(styles.preserveWhiteSpace)}>
              <Text>{specialInstructions}</Text>
            </p>
          ),
          title: 'Lender Instructions',
        }
      : null;

  if (principalType === 'notary') {
    if (view !== 'PROFILE_CREDENTIALS') {
      return compact([lenderInstructions]);
    }

    // This renders on the credentials tab
    const staticTasks = map(openTasks, task => getTask(task, initialize, featureFlags));

    return [
      {
        content: <>{staticTasks}</>,
        title: 'Uploads',
      },
    ];
  } else {
    const [failedTasks, successfulTasks] = partition(openTasks, 'error');
    const requiredTasksConfigs = reject(successfulTasks, { isOptional: true });
    const optionalTaskConfigs = filter(successfulTasks, task => !!task.isOptional);

    const errors = map(failedTasks, 'taskHeader').join(', ');
    const errorMessage = `${failedTasks.length} task(s) have failed: ${errors}. Please refresh the page or try again later.`;

    const optionalTasks = optionalTaskConfigs.map(optionalTask =>
      getTask(optionalTask, initialize, featureFlags, closingType),
    );

    const failedTask = isEmpty(failedTasks) ? null : <TaskError errorMessage={errorMessage} showDismiss={true} />;
    const requiredTasks = isEmpty(requiredTasksConfigs)
      ? null
      : map(requiredTasksConfigs, task => getTask(task, initialize));

    const noTasks = (
      <p className={css(styles.noOpenTaskContainer)}>
        <Text>Nothing here, you are all caught up!</Text>
      </p>
    );

    return compact([
      lenderInstructions,
      {
        content: (
          <>
            {failedTask}
            {requiredTasks}
            {failedTask || requiredTasks ? null : noTasks}
            {optionalTasks}
          </>
        ),
        title: 'Tasks',
      },
    ]);
  }
};

const getTask = (
  task: BlendBridge.API.Task,
  onSuccess: () => Promise<any>,
  _featureFlags?: BlendBridge.API.DCSFlags,
  closingType?: ClosingType,
) => {
  const {
    afterTask,
    buttonLabel,
    columnedTaskDescription,
    disabledReason,
    displayType,
    isDisabled,
    isOptional,
    taskDescription,
    taskHeader,
    taskSubheader,
    taskPreOpeningSubheader,
    taskId,
  } = task;
  switch (task.type) {
    case BlendBridge.TaskTypes.FORM:
      const { isModalSubmitButtonDisabled, modalSubmitButtonDisabledReason, modalSubmitButtonLabel } = task;

      return (
        <FormTask
          buttonLabel={buttonLabel}
          columnedTaskDescription={columnedTaskDescription}
          disabledReason={disabledReason}
          displayType={displayType || (isOptional === true ? 'link' : 'card')}
          isDisabled={isDisabled}
          isModalSubmitButtonDisabled={isModalSubmitButtonDisabled}
          key={taskHeader}
          modalSubmitButtonDisabledReason={modalSubmitButtonDisabledReason}
          modalSubmitButtonLabel={modalSubmitButtonLabel}
          onSuccess={onSuccess}
          taskDescription={taskDescription}
          taskHeader={taskHeader}
          taskId={taskId}
          taskPreOpeningSubheader={taskPreOpeningSubheader}
          taskSubheader={taskSubheader}
        />
      );
    case BlendBridge.TaskTypes.REDIRECT:
      const { redirectUrl, urlOpenMethod } = task;
      // TODO remove this logic once Eclose starts creating TAGGING task and
      // we run migration to update REDIRECT task to TAGGING task.
      // buttonLabel === Tag is set in Eclose onLoad
      if (buttonLabel === 'Tag') {
        return (
          <TaggingTask
            buttonLabel={buttonLabel}
            disabledReason={disabledReason}
            isDisabled={isDisabled}
            key={taskHeader}
            onSuccess={onSuccess}
            taskHeader={taskHeader}
            taskId={taskId!}
            type={BlendBridge.TaskTypes.TAGGING}
            urlOpenMethod={urlOpenMethod}
          />
        );
      }
      return (
        <RedirectTask
          buttonLabel={buttonLabel}
          disabledReason={disabledReason}
          isDisabled={isDisabled}
          key={taskHeader}
          onSuccess={onSuccess}
          redirectUrl={redirectUrl}
          taskHeader={taskHeader}
          taskId={taskId!}
          type={task.type}
          urlOpenMethod={urlOpenMethod}
        />
      );
    case BlendBridge.TaskTypes.TAGGING:
      return (
        <TaggingTask
          buttonLabel={buttonLabel}
          disabledReason={disabledReason}
          isDisabled={isDisabled}
          key={taskHeader}
          onSuccess={onSuccess}
          taskHeader={taskHeader}
          taskId={taskId!}
          type={task.type}
          urlOpenMethod={task.urlOpenMethod}
        />
      );
    case BlendBridge.TaskTypes.UPLOAD_DOCUMENT:
      const { documentType, uploadLimit, footerText } = task;
    case BlendBridge.TaskTypes.UPLOAD_FILE:
      const { allowMultipleUploads, closingDesignation, sourceType } = task;
      if (isOptional) {
        return <OptionalUploadTask key={task.taskHeader} {...task} closingType={closingType} onSuccess={onSuccess} />;
      }

      return (
        <DocUploadTask
          afterTask={afterTask}
          allowMultipleUploads={allowMultipleUploads}
          allowedFileExtensions={task.allowedFileExtensions}
          buttonLabel={buttonLabel}
          closingDesignation={closingDesignation}
          disabledReason={disabledReason}
          documentType={documentType}
          footerText={footerText}
          isDisabled={isDisabled}
          key={taskHeader}
          onSuccess={onSuccess}
          prompt={
            taskDescription && (
              <>
                {map(taskDescription, descriptor => {
                  const key = generateTaskDescriptionKey(descriptor);
                  return <TaskDescription displayInline={true} key={key} taskDescription={descriptor} />;
                })}
              </>
            )
          }
          sourceType={sourceType}
          taskHeader={taskHeader}
          taskId={taskId}
          taskPreOpeningSubheader={taskPreOpeningSubheader}
          uploadLimit={uploadLimit}
        />
      );
    case BlendBridge.TaskTypes.SHARE:
      const { roleOptions } = task;
      return (
        <ShareTask
          buttonLabel={buttonLabel}
          disabledReason={disabledReason}
          isDisabled={isDisabled}
          key={taskHeader}
          onSuccess={onSuccess}
          roleOptions={roleOptions}
          taskDescription={taskDescription}
          taskHeader={taskHeader}
          taskId={taskId}
          taskSubheader={taskSubheader}
        />
      );
    case BlendBridge.TaskTypes.SELECT_RON_PROVIDER:
      return (
        <SelectRONProviderTask
          buttonLabel={buttonLabel}
          disabledReason={disabledReason}
          isBlendDisabled={task.isBlendDisabled}
          isDisabled={isDisabled}
          key={taskHeader}
          onSuccess={onSuccess}
          taskDescription={taskDescription}
          taskHeader={taskHeader}
          taskId={taskId}
        />
      );
    case BlendBridge.TaskTypes.SCHEDULE_CLOSING_APPOINTMENT:
      return (
        <ScheduleClosingAppointmentTask
          borrowerPreferredAddress={task.borrowerPreferredAddress}
          borrowerPreferredMethod={task.borrowerPreferredMethod}
          borrowerPreferredTimezone={task.borrowerPreferredTimezone}
          buttonLabel={buttonLabel}
          closingDate={task.closingDate}
          closingType={task.closingType}
          disabledReason={disabledReason}
          isDisabled={isDisabled}
          key={taskHeader}
          notarizationMethodOptions={task.notarizationMethodOptions}
          onSuccess={onSuccess}
          taskDescription={taskDescription}
          taskHeader={taskHeader}
          taskId={taskId}
          timeOptions={task.timeOptions}
          timezoneOptions={task.timezoneOptions}
          type={task.type}
        />
      );
    case BlendBridge.TaskTypes.RELEASE_CLOSING_DOCUMENTS:
      return (
        <ReleaseClosingDocumentsTask
          disabledReason={disabledReason}
          isDisabled={isDisabled}
          key={taskHeader}
          onSuccess={onSuccess}
          taskHeader={taskHeader}
          taskId={taskId!}
        />
      );
    case BlendBridge.TaskTypes.CANCEL_CLOSING_DOCUMENTS:
      return (
        <CancelClosingDocumentsTask
          disabledReason={disabledReason}
          isDisabled={isDisabled}
          onSuccess={onSuccess}
          taskHeader={taskHeader}
          taskId={taskId!}
        />
      );
    default:
      // hold over until all tasks have a type
      if (isOptional) {
        return <OptionalUploadTask key={taskHeader} {...(task as any)} onSuccess={onSuccess} />;
      }
      return null;
  }
};
